import { type BillingApi } from '@/features/billing/api/billing-api'
import { Coupon, type CouponType } from './Coupon'
import { collection } from '@/infra/collection'
import type { UserCouponResource } from '@/features/billing/api/resources/coupon'
import { UserCoupon, type UserCouponProps } from './UserCoupon'
import { NotFound } from 'fejl'
import { Id } from '@/infra/id'
import { type Member } from '@/features/iam/members/Member'
import { type CursorPaged, type CursorPageQuery } from '@/infra/paging'
import { maybeParseDate, unixEpoch } from '@/utils/date-util'
import {
  mapCouponListStatusToResource,
  mapCouponResourceToProps,
  mapCouponTypeToResource,
} from './coupon-mappers'

/**
 * Coupon store.
 */
export class CouponStore {
  /**
   * The coupons themselves.
   */
  readonly coupons = collection({
    getDataId: (d) => d.id,
    getModelId: (m) => m.id,
    create: () => new Coupon(),
  })

  /**
   * The user<->coupon relations.
   */
  readonly userCoupons = collection({
    getDataId: (d) => d.id,
    getModelId: (m) => m.id,
    create: () => new UserCoupon(),
  })

  constructor(private readonly _api: BillingApi) {}

  /**
   * Fetches the coupons of a member.
   */
  async fetchUserCoupons(member: Member, signal?: AbortSignal) {
    const data = await this._api.userCoupons.list(
      Id.assert(member.workspace.id),
      Id.assert(member.user.publicId),
      signal,
    )
    return this.userCoupons.set(
      data.map((d) => this._mapUserCouponResourceToProps(d)),
    )
  }

  /**
   * Adds the coupon with the given code to the member. Resolves to `null`
   * when there is no such coupon.
   */
  async addCouponToUser(
    member: Member,
    code: string,
  ): Promise<UserCoupon | null> {
    const data = await this._api.userCoupons
      .add(
        Id.assert(member.workspace.id),
        Id.assert(member.user.publicId),
        code,
      )
      .catch(NotFound.ignore(null))
    return data
      ? this.userCoupons.set(this._mapUserCouponResourceToProps(data))
      : null
  }

  /**
   * Fetches a coupon by ID.
   */
  async fetchCoupon(id: Id, signal?: AbortSignal) {
    const data = await this._api.coupons.get(Id.assert(id), signal)
    return this.coupons.set(mapCouponResourceToProps(data))
  }

  /**
   * Fetches a workspace's coupon by code.
   */
  async fetchCouponByCode(code: string, workspaceId: Id, signal?: AbortSignal) {
    const data = await this._api.coupons.getByCode(
      Id.assert(workspaceId),
      code,
      signal,
    )
    return this.coupons.set(mapCouponResourceToProps(data))
  }

  /**
   * Lists coupons.
   */
  async listCoupons(
    query: ListCouponsParams,
    signal?: AbortSignal,
  ): Promise<CursorPaged<Coupon>> {
    const response = await this._api.coupons.list(
      Id.assert(query.workspaceId),
      {
        after: query.after,
        limit: query.limit,
        coupon_type:
          query.couponType && mapCouponTypeToResource(query.couponType),
        status: query.status && mapCouponListStatusToResource(query.status),
        search_text: query.searchText,
      },
      signal,
    )
    return {
      next: response.cursor,
      total: response.total,
      data: this.coupons.set(response.data.map(mapCouponResourceToProps)),
    }
  }

  private _mapUserCouponResourceToProps(
    resource: UserCouponResource,
  ): UserCouponProps {
    return {
      id: resource.id,
      redeemed: resource.redeemed,
      userId: resource.user_id,
      coupon: this.coupons.set(mapCouponResourceToProps(resource.coupon)),
      workspaceId: resource.coupon.workspace_id,
      dateCreated: maybeParseDate(resource.date_created) ?? unixEpoch(),
      dateRedeemed: maybeParseDate(resource.date_redeemed) ?? unixEpoch(),
    }
  }
}

/**
 * Status to list coupons by.
 */
export type CouponListStatus = 'Active' | 'Expired' | 'All' | 'Redeemed'

/**
 * Query for listing coupons.
 */
export interface ListCouponsParams extends CursorPageQuery {
  workspaceId: Id
  couponType?: CouponType['type']
  status?: CouponListStatus
  searchText?: string
}
